import Alert from '@risk-smart/themed-cloudscape-components/alert';
import Box from '@risk-smart/themed-cloudscape-components/box';
import FileUpload from '@risk-smart/themed-cloudscape-components/file-upload';
import { humanFileSize } from '@risksmart-app/components/src/file/fileUtils';
import { allowedFileExtensions } from '@risksmart-app/shared/allowedFileExtensions';
import type { FC } from 'react';

import type { FilesFromForm } from '@/components/form/controlled-file-list/types';
import controlledFileUploadStyles from '@/components/form/controlled-file-upload/style.module.scss';
import { FormField } from '@/components/form/form/FormField';
import { useFileDownload } from '@/hooks/mutations/file/useFileDownload';

import type { CustomAttributeProps } from './CustomAttributeProps';

/**
 * Custom-attribute "Attach file" field. Behaves like the entity-level Attach
 * Files control (`ControlledFileUpload`/`ControlledFileList`) but is driven by
 * the custom-attribute `value`/`onChange` contract rather than react-hook-form
 * `control`/`name`.
 *
 * The value is a mixed list of freshly-selected `File` objects and
 * already-persisted `RelationFile` records (hydrated from the CUS-78 junction
 * by `useCustomAttributeFileSave`). That hook uploads the new files and links
 * them per field via `customAttributeFiles.sync` after the entity save — the
 * files are NOT stored in CustomAttributeData.
 */
export const CustomAttributeFileUpload: FC<
  CustomAttributeProps<FilesFromForm | null>
> = ({ value, onChange, label, disabled, error, description, schema }) => {
  const downloadFile = useFileDownload();
  const multiple = schema.maxItems !== 1;

  const files = (value ?? []).filter(
    (f): f is NonNullable<typeof f> => f != null
  );

  return (
    <FormField
      label={label}
      errorText={error}
      guidance={description}
      stretch
      testId={label}
    >
      {!disabled && (
        <FileUpload
          {...{ className: controlledFileUploadStyles.hideFiles }}
          accept={allowedFileExtensions.join(',')}
          value={files.map((f) =>
            f instanceof File ? f : new File([], f.FileName)
          )}
          onChange={(e) => {
            const newFiles = e.detail.value;
            onChange(multiple ? [...files, ...newFiles] : newFiles);
          }}
          showFileLastModified
          showFileSize
          multiple={multiple}
          i18nStrings={{
            uploadButtonText: (e) => (e ? 'Choose files' : 'Choose file'),
            dropzoneText: (e) =>
              e ? 'Drop files to upload' : 'Drop file to upload',
            removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
            limitShowFewer: 'Show fewer files',
            limitShowMore: 'Show more files',
            errorIconAriaLabel: 'Error',
          }}
        />
      )}

      <div>
        {files.map((f) => {
          const itemProps: {
            fileId?: string;
            file?: File;
            fileName: string;
            fileSize: number;
          } =
            f instanceof File
              ? {
                  file: f,
                  fileName: f.name,
                  fileSize: f.size,
                }
              : {
                  fileId: f.Id,
                  fileName: f.FileName,
                  fileSize: f.FileSize,
                };

          return (
            <Box
              data-testid={'file-list-file-item'}
              margin={{ vertical: 'xs' }}
              key={itemProps.fileId ?? itemProps.file?.name}
            >
              <Alert
                type={error ? 'error' : 'success'}
                dismissible={!disabled}
                onDismiss={() => onChange(files.filter((ff) => ff !== f))}
              >
                <div
                  onClick={async () => {
                    await downloadFile({
                      fileId: itemProps.fileId,
                      fileName: itemProps.fileName,
                      file: itemProps.file,
                    });
                  }}
                >
                  <span data-testid={'file-name'}>{itemProps.fileName}</span>
                  <br />
                  <Box variant={'small'}>
                    {humanFileSize(itemProps.fileSize)}
                  </Box>
                </div>
              </Alert>
            </Box>
          );
        })}
      </div>
    </FormField>
  );
};
